import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import api, { flushOfflineApiQueue } from "@/lib/api";
import {
  clearOfflineSession,
  clearOfflineSignedOut,
  isOfflineSignedOut,
  loadOfflineSession,
  markOfflineSignedOut,
  saveOfflineCredential,
  saveOfflineSession,
  setOfflineIdentity,
  verifyOfflineCredential,
} from "@/lib/offlineStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);

  /*
   * Backend-controlled navigation state.
   *
   * server.py provides these values through /auth/login and /auth/me.
   */
  const [landingPath, setLandingPath] = useState("/dashboard");
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const [impersonation, setImpersonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offlineMode, setOfflineMode] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const companyName = tenant?.company_name?.trim();
    document.title = companyName
      ? `${companyName} | EGS BUSINESS SUITE`
      : "EGS BUSINESS SUITE";
  }, [tenant?.company_name]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    let icon = document.querySelector('link[rel="icon"]');
    if (!icon) {
      icon = document.createElement("link");
      icon.rel = "icon";
      document.head.appendChild(icon);
    }
    icon.href = tenant?.logo_url || "/branding/egs-logo.jpg";
  }, [tenant?.logo_url]);

  /*
   * ------------------------------------------------------------------------
   * Apply authentication state returned by the backend
   * ------------------------------------------------------------------------
   */

  const applyAuthState = useCallback((data) => {
    setUser(data?.user || null);
    setTenant(data?.tenant || null);
    setOfflineMode(Boolean(data?.offline_mode));
    setOfflineIdentity(data?.user, data?.tenant);
    if (data?.user?.user_id && data?.tenant?.tenant_id) saveOfflineSession(data).catch(() => {});
    if (!data?.offline_mode) clearOfflineSignedOut().catch(() => {});

    setLandingPath(
      data?.landing_path || "/dashboard"
    );

    setMustChangePassword(
      Boolean(data?.must_change_password)
    );

    setImpersonation(
      data?.impersonating
        ? {
            home_tenant: data.home_tenant,
          }
        : null
    );
  }, []);

  /*
   * ------------------------------------------------------------------------
   * Check current authentication
   * ------------------------------------------------------------------------
   */

  const checkAuth = useCallback(async () => {
    try {
      const { data, offline } = await api.get("/auth/me");
      if (offline && await isOfflineSignedOut()) {
        setUser(null);
        setTenant(null);
        setOfflineMode(false);
        setOfflineIdentity(null, null);
        setLandingPath("/dashboard");
        setMustChangePassword(false);
        setImpersonation(null);
        return;
      }
      applyAuthState({ ...data, offline_mode: Boolean(offline) });
    } catch (error) {
      const canRestoreOffline = !error?.response || [502, 503, 504].includes(error.response.status);
      if (canRestoreOffline) {
        try {
          const saved = await loadOfflineSession();
          if (saved?.user?.user_id && saved?.tenant?.tenant_id) {
            applyAuthState({ ...saved, offline_mode: true });
            return;
          }
        } catch { /* Continue to signed-out state when saved auth cannot be opened. */ }
      }
      setUser(null);
      setTenant(null);
      setOfflineMode(false);
      setOfflineIdentity(null, null);
      setLandingPath("/dashboard");
      setMustChangePassword(false);
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, [applyAuthState]);

  /*
   * ------------------------------------------------------------------------
   * Initial authentication check
   * ------------------------------------------------------------------------
   */

  useEffect(() => {
    /*
     * Skip /me check when returning from Emergent OAuth.
     * AuthCallback handles the session exchange.
     */

    if (
      typeof window !== "undefined" &&
      window.location.hash?.includes("session_id=")
    ) {
      setLoading(false);
      return;
    }

    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user?.user_id) return undefined;

    const recordActivity = () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      api.post("/auth/activity").catch(() => {});
    };

    recordActivity();
    const interval = window.setInterval(recordActivity, 30_000);
    document.addEventListener("visibilitychange", recordActivity);
    window.addEventListener("focus", recordActivity);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", recordActivity);
      window.removeEventListener("focus", recordActivity);
    };
  }, [user?.user_id]);

  useEffect(() => {
    if (!user?.user_id) return undefined;
    let active = true;
    const retryPendingWrites = () => {
      if (!active || !navigator.onLine || document.visibilityState === "hidden") return;
      flushOfflineApiQueue().catch(() => {});
    };
    retryPendingWrites();
    const retryTimer = window.setInterval(retryPendingWrites, 60000);
    window.addEventListener("online", retryPendingWrites);
    window.addEventListener("egs-network-online", retryPendingWrites);
    window.addEventListener("focus", retryPendingWrites);
    document.addEventListener("visibilitychange", retryPendingWrites);
    return () => {
      active = false;
      window.clearInterval(retryTimer);
      window.removeEventListener("online", retryPendingWrites);
      window.removeEventListener("egs-network-online", retryPendingWrites);
      window.removeEventListener("focus", retryPendingWrites);
      document.removeEventListener("visibilitychange", retryPendingWrites);
    };
  }, [user?.user_id]);

  /*
   * ------------------------------------------------------------------------
   * Login
   * ------------------------------------------------------------------------
   */

  const login = async (email, password, totpCode) => {
    const { data } = await api.post("/auth/login", {
      email,
      password,
      ...(totpCode ? { totp_code: totpCode } : {}),
    });

    if (data.totp_required || data.totp_setup_required) return data;
    applyAuthState(data);
    await saveOfflineCredential(email, password, data).catch(() => {});

    return data;
  };

  const loginOffline = async (identifier, password) => {
    const saved = await loadOfflineSession({ allowSignedOut: true });
    if (!saved || !await verifyOfflineCredential(identifier, password, saved)) {
      throw new Error("Offline sign-in is unavailable or the credentials do not match this device.");
    }
    await clearOfflineSignedOut();
    applyAuthState({ ...saved, offline_mode: true });
    return saved;
  };

  const rememberOfflineCredential = useCallback(async (identifier, password) => {
    const session = { user, tenant, landing_path: landingPath, must_change_password: false };
    await saveOfflineSession(session);
    await saveOfflineCredential(identifier, password, session);
  }, [user, tenant, landingPath]);

  /*
   * ------------------------------------------------------------------------
   * Register
   * ------------------------------------------------------------------------
   */

  const register = async (payload) => {
    const { data } = await api.post(
      "/auth/register",
      payload
    );

    applyAuthState(data);

    return data.user;
  };

  /*
   * ------------------------------------------------------------------------
   * Logout
   * ------------------------------------------------------------------------
   */

  const logout = async () => {
    let serverLogoutConfirmed = false;
    try {
      await api.post("/auth/logout");
      serverLogoutConfirmed = true;
    } catch { /* Preserve a local recovery path when the server cannot confirm logout. */ }
    if (serverLogoutConfirmed) await clearOfflineSession().catch(() => {});
    else await markOfflineSignedOut().catch(() => {});
    setOfflineIdentity(null, null);

    setUser(null);
    setTenant(null);
    setOfflineMode(false);
    setLandingPath("/dashboard");
    setMustChangePassword(false);
    setImpersonation(null);
  };

  /*
   * ------------------------------------------------------------------------
   * Refresh tenant/authentication state
   * ------------------------------------------------------------------------
   */

  const refreshTenant = useCallback(async () => {
    const { data } = await api.get("/auth/me");

    applyAuthState(data);
  }, [applyAuthState]);

  /*
   * ------------------------------------------------------------------------
   * Workspace impersonation
   * ------------------------------------------------------------------------
   */

  const enterWorkspace = async (tenantId) => {
    await api.post(
      `/admin/tenants/${tenantId}/enter`
    );

    await refreshTenant();
  };

  const exitWorkspace = async () => {
    await api.post("/admin/exit-workspace");

    await refreshTenant();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        tenant,
        offlineMode,

        /*
         * Backend-controlled navigation
         */
        landingPath,
        mustChangePassword,

        impersonation,

        loading,

        login,
        loginOffline,
        rememberOfflineCredential,
        register,
        logout,

        checkAuth,
        refreshTenant,
        applyAuthState,

        setUser,
        setTenant,

        enterWorkspace,
        exitWorkspace,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}